import { useTranslation } from 'react-i18next';
/**
 * GlobalSearch — Ctrl-K / Cmd-K command palette that searches every
 * record type the user has access to and jumps straight to it.
 *
 * Results come from the server already grouped and permission-filtered,
 * so cmdk's own filtering is switched off (`shouldFilter={false}`).
 * On phones the palette fills the screen and rows grow to thumb size,
 * because engineers use this on site.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import {
  Briefcase, Building2, Users, Wrench, ClipboardCheck, FileSpreadsheet,
  AlertTriangle, FileSignature, MessageSquareQuote, Loader2, CornerDownLeft,
  History,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 250;

const TYPE_ICON = {
  project: Briefcase,
  customer: Building2,
  employee: Users,
  task: Wrench,
  checklist: ClipboardCheck,
  test_report: FileSpreadsheet,
  damage: AlertTriangle,
  document: FileSignature,
  survey: MessageSquareQuote,
};

export const GlobalSearch = ({ open, onOpenChange, onNavigate }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setGroups([]);
      setLoading(false);
      return;
    }
    axios.get(`${API}/search/recent`)
      .then((r) => setRecent(r.data?.recent || []))
      .catch(() => setRecent([]));
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setGroups([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/search`, { params: { q: term } });
        if (id === reqId.current) setGroups(r.data?.groups || []);
      } catch {
        if (id === reqId.current) setGroups([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const go = useCallback((item) => {
    onOpenChange(false);
    // Only remember searches that actually led somewhere.
    const term = query.trim();
    if (term.length >= 2) {
      axios.post(`${API}/search/recent`, { q: term })
        .then((r) => setRecent(r.data?.recent || []))
        .catch(() => {});
    }
    const ctx = {};
    if (item.project_id) {
      ctx.openProjectId = item.project_id;
      ctx.projectTab = item.project_tab || 'details';
    }
    if (item.search_term) {
      if (item.nav_tab === 'customers') ctx.searchCustomer = item.search_term;
      else if (item.nav_tab === 'employees') ctx.searchEmployee = item.search_term;
      else if (item.nav_tab === 'contracts') ctx.searchContract = item.search_term;
    }
    onNavigate(item.nav_tab, Object.keys(ctx).length ? ctx : null);
  }, [onNavigate, onOpenChange, query]);

  const clearRecent = useCallback(async () => {
    setRecent([]);
    await axios.delete(`${API}/search/recent`).catch(() => {});
  }, []);

  const hasResults = groups.some((g) => g.items.length > 0);
  const idle = query.trim().length < 2;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} commandProps={{ shouldFilter: false }}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={t('auto.misc2.searchChecklistsReportsDocumentsPeople')}
        data-testid="global-search-input"
      />
      <CommandList
        className="max-h-[calc(100dvh-8rem)] sm:max-h-[400px]"
        data-testid="global-search-results"
      >
        {loading && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />{t('auto.misc2.searching')}
          </div>
        )}

        {!loading && idle && recent.length > 0 && (
          <CommandGroup
            heading={(
              <span className="flex items-center justify-between">
                {t('auto.misc2.recentSearches')}
                <button
                  type="button"
                  onClick={clearRecent}
                  className="text-xs font-normal text-muted-foreground underline-offset-2 hover:underline"
                  data-testid="global-search-clear-recent"
                >
                  {t('auto.misc2.clear')}
                </button>
              </span>
            )}
          >
            {recent.map((term) => (
              <CommandItem
                key={term}
                value={`recent-${term}`}
                onSelect={() => setQuery(term)}
                className="group cursor-pointer py-3 sm:py-2"
                data-testid={`global-search-recent-${term}`}
              >
                <History className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{term}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!loading && idle && recent.length === 0 && (
          <div className="py-8 px-4 text-center text-sm text-muted-foreground" data-testid="global-search-hint">
            Type at least 2 characters. Try a checklist number, a part
            reference, a person or a project.
          </div>
        )}

        {!loading && !idle && !hasResults && (
          <CommandEmpty data-testid="global-search-empty">
            Nothing found for “{query.trim()}”.
          </CommandEmpty>
        )}

        {!loading && groups.map((group) => {
          const Icon = TYPE_ICON[group.type] || Briefcase;
          return (
            <CommandGroup key={group.type} heading={group.label}>
              {group.items.map((item) => (
                <CommandItem
                  key={`${group.type}-${item.id}`}
                  value={`${group.type}-${item.id}`}
                  onSelect={() => go(item)}
                  className="group cursor-pointer flex-wrap gap-x-2 py-3 sm:flex-nowrap sm:py-2"
                  data-testid={`global-search-result-${group.type}-${item.id}`}
                >
                  <Icon className="h-4 w-4 text-[#AA0000] shrink-0" />
                  <span className="font-medium truncate">{item.title}</span>
                  {item.subtitle ? (
                    <span className="order-last w-full basis-full pl-6 text-xs text-muted-foreground truncate sm:order-none sm:w-auto sm:basis-auto sm:pl-0">
                      {item.subtitle}
                    </span>
                  ) : null}
                  {item.meta ? (
                    <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {item.meta}
                    </span>
                  ) : null}
                  <CornerDownLeft className="hidden sm:block h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-data-[selected=true]:opacity-100" />
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
};

/** Registers the Cmd-K / Ctrl-K shortcut and returns palette open state. */
export const useGlobalSearchHotkey = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key?.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return [open, setOpen];
};

export default GlobalSearch;
